import { Chip, ChipProps } from "@mui/material"
import { makeStyles, tss } from "tss-react/mui"
import { PropsWithChildren } from "react"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"

type StyleProps = Readonly<{
    placement: "standalone" | "table"
}>

const useStyles = tss.withParams<StyleProps>().create(({ theme, placement }) => ({
    placementStandalone: {
        borderRadius: theme.shape.borderRadius * 2,
        "& .MuiChip-label": {
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 500,
        },
    },
    placementTable: {
        height: "auto",
        paddingTop: "4px",
        paddingBottom: "4px",
        "& .MuiChip-label": {
            paddingLeft: "8px",
            paddingRight: "8px",
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 500,
        },
        borderRadius: "12px",
    },
    colorGreen: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.green[300] : tailwindPalette.green[100]}`,
        backgroundColor: tailwindPalette.green[50],
        color: tailwindPalette.green[700],
    },
    colorSky: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.sky[300] : tailwindPalette.sky[100]}`,
        backgroundColor: tailwindPalette.sky[50],
        color: tailwindPalette.sky[700],
    },
    colorSlate: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.slate[300] : tailwindPalette.slate[100]}`,
        backgroundColor: tailwindPalette.slate[50],
        color: tailwindPalette.slate[500],
    },
    colorRed: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.red[300] : tailwindPalette.red[100]}`,
        backgroundColor: tailwindPalette.red[50],
        color: tailwindPalette.red[700],
    },
    colorAmber: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.amber[300] : tailwindPalette.amber[100]}`,
        backgroundColor: tailwindPalette.amber[50],
        color: tailwindPalette.amber[700],
    },
    colorIndigo: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.indigo[300] : tailwindPalette.indigo[100]}`,
        backgroundColor: tailwindPalette.indigo[50],
        color: tailwindPalette.indigo[700],
    },
    colorPurple: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.purple[400] : tailwindPalette.purple[300]}`,
        backgroundColor: tailwindPalette.purple[200],
        color: tailwindPalette.purple[900],
    },
    colorPink: {
        border: `1px solid ${placement === "standalone" ? tailwindPalette.pink[300] : tailwindPalette.pink[100]}`,
        backgroundColor: tailwindPalette.pink[50],
        color: tailwindPalette.pink[700],
    },
}))

export type StandardChipVariantType = Readonly<
    | "grey"
    | "primary"
    | "secondary"
    | "red"
    | "info"
    | "success"
    | "warning"
    | "yellow"
    | "purple"
    | "purpleLight"
    | "pink"
    | "neutral"
    | "white-outline"
    | "black"
    | undefined
>

export type Placement = "table" | "standalone"

type StandardChipProps = Readonly<
    PropsWithChildren<{
        className?: string
        label?: string
        variant: StandardChipVariantType
        placement?: Placement
    }> &
        Pick<ChipProps, "size">
>

export function StandardChip({ label, className, variant, children, size, placement = "standalone" }: StandardChipProps) {
    const { l10n } = useAppLocalization()
    const { classes, cx } = useStyles({ placement })

    const labelToUse = children ?? label

    switch (variant) {
        case "success":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorGreen, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )
        case "grey":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorSlate, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )

        case "info":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorSky, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )

        case "red":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorRed, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )
        case "yellow":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorAmber, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )
        case "purple":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorIndigo, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )
        case "purpleLight":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorPurple, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                    sx={{ border: "1px solid #C084FC", backgroundColor: "#E9D5FF", color: "#581C87" }}
                />
            )
        case "pink":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.colorPink, {[classes.placementStandalone]: placement === "standalone", [classes.placementTable]: placement === "table" })}
                />
            )
        case "neutral":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.placementStandalone)}
                    sx={{ border: "1px solid #475569", backgroundColor: "transparent", color: "#334155" }}
                />
            )
        case "white-outline":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.placementStandalone)}
                    sx={{ border: "1px solid #fff", backgroundColor: "transparent", color: "#fff" }}
                />
            )
        case "black":
            return (
                <Chip
                    label={labelToUse}
                    size={size}
                    className={cx(className, classes.placementStandalone)}
                    sx={{ border: "1px solid #000", backgroundColor: "#000", color: "#fff" }}
                />
            )
        default:
            return <></>
    }
}
